<template>
  <div class="auth-section">
    <!-- Login Form -->
    <LoginForm
      v-if="currentView === 'login'"
      :show-right-panel="true"
      :show-logo-link="false"
      :enable-redirect="false"
      :load-saved-credentials="true"
      :show-query-error="false"
      :use-popup-mode="true"
      :show-guest-login="true"
      :default-redirect-url="computedRedirectUrl"
      @login-success="handleLoginSuccess"
      @login-error="handleLoginError"
      @goto-signup="goToRegister"
      @google-login="handleGoogleLogin"
      @goto-forgot-password="goToForgotPassword"
      @goto-guest-login="goToGuestLogin"
      description-text="Create an account to save your carts, access all activities, and get updates."
    />

    <!-- Register Form -->
    <RegisterForm
      v-if="currentView === 'register'"
      :show-left-panel="true"
      :show-logo-link="false"
      :show-language-selector="false"
      :show-recaptcha="true"
      :enable-redirect="false"
      :use-popup-mode="true"
      :redirect-url="computedRedirectUrl"
      @register-success="handleRegisterSuccess"
      @register-error="handleRegisterError"
      @goto-login="goToLogin"
      @google-register="handleGoogleRegister"
      @goto-email-verification="goToEmailVerification"
    />

    <!-- Email Verification Form -->
    <EmailVerificationForm
      v-if="currentView === 'email-verification'"
      :show-left-panel="true"
      :show-logo-link="false"
      :enable-redirect="false"
      :email="registeredEmail"
      :load-email-from-storage="false"
      :success-message="'You can now continue your quote request!'"
      :show-success-button="false"
      @goto-login="goToLogin"
      @resend-success="handleResendSuccess"
      @resend-error="handleResendError"
      @email-verified="handleEmailVerified"
    />

    <!-- Forgot Password Form -->
    <ForgotPasswordForm
      v-if="currentView === 'forgot-password'"
      :show-right-panel="true"
      :show-logo-link="false"
      :show-language-selector="false"
      :show-background-images="false"
      :show-recaptcha="true"
      :enable-redirect="false"
      @forgot-password-success="handleForgotPasswordSuccess"
      @forgot-password-error="handleForgotPasswordError"
      @goto-login="goToLogin"
      @goto-signup="goToRegister"
      @email-sent="handleEmailSent"
    />

    <!-- Guest Login Form -->
    <GuestLoginForm
      v-if="currentView === 'guest-login'"
      :show-right-panel="true"
      :show-logo-link="false"
      :show-language-selector="false"
      :show-background-images="false"
      :enable-redirect="false"
      @guest-login-success="handleGuestLoginSuccess"
      @guest-login-error="handleGuestLoginError"
      @goto-login="goToLogin"
      @goto-signup="goToRegister"
    />
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  defineProps,
  defineEmits,
  defineExpose,
  inject,
} from "vue";
import EmailVerificationForm from "@/components/EmailVerificationForm.vue";
import ForgotPasswordForm from "@/components/ForgotPasswordForm.vue";
import GuestLoginForm from "@/components/GuestLoginForm.vue";
import LoginForm from "@/components/LoginForm.vue";
import RegisterForm from "@/components/RegisterForm.vue";
import { useUserState } from "@/composeables/useUserState";
import { useRoute } from "vue-router";

const props = defineProps({
  // Controls which view to show initially
  initialView: {
    type: String,
    default: "login", // 'login', 'register', 'email-verification', 'forgot-password', 'guest-login'
    validator: (value) =>
      [
        "login",
        "register",
        "email-verification",
        "forgot-password",
        "guest-login",
      ].includes(value),
  },
  redirectUrl: {
    type: String,
    default: "",
  },
});

const notify = inject("notify");

const { updateUserState } = useUserState();
const route = useRoute();

const emit = defineEmits([
  "login-success",
  "register-success",
  "email-verified",
  "password-reset-sent",
  "guest-login-success",
  "guest-login-error",
]);

// State management
const currentView = ref(props.initialView);
const registeredEmail = ref("");
const computedRedirectUrl = ref("");

// Function to build redirect URL from current route
function buildRedirectUrl() {
  if (props.redirectUrl) {
    return props.redirectUrl;
  }

  // Build URL from route if no explicit redirectUrl prop
  const baseUrl = window.location.origin + route.path;
  const queryParams = new URLSearchParams(route.query).toString();
  return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
}

// Watch for initial view changes
watch(
  () => props.initialView,
  (newView) => {
    currentView.value = newView;
  }
);

// Set redirect URL when component is mounted
computedRedirectUrl.value = buildRedirectUrl();

// Navigation methods
function goToLogin() {
  currentView.value = "login";
}

function goToRegister() {
  currentView.value = "register";
}

function goToEmailVerification(email = "") {
  if (email) {
    registeredEmail.value = email;
  }
  currentView.value = "email-verification";
}

function goToForgotPassword() {
  currentView.value = "forgot-password";
}

function goToGuestLogin() {
  currentView.value = "guest-login";
}

function handleLoginSuccess(data) {
  notify.plain("You're successfully logged in!");
  emit("login-success", data);
}

function handleLoginError(error) {
  console.log("Auth section login error:", error);
}

function handleGoogleLogin() {
  console.log("Google login initiated from auth section");
}

function handleRegisterSuccess(data) {
  console.log("Auth section register successful:", data);
  emit("register-success", data);
  goToEmailVerification(data.email);
}

function handleRegisterError(error) {
  console.log("Auth section register error:", error);
}

function handleGoogleRegister() {
  console.log("Google register initiated from auth section");
}

function handleResendSuccess(email) {
  console.log("Resend verification successful for:", email);
}

function handleResendError(error) {
  console.log("Resend verification error:", error);
}

async function handleEmailVerified(data) {
  await updateUserState();
  notify.plain("Your email has been verified successfully!");
  emit("email-verified", data);
}

function handleForgotPasswordSuccess(email) {
  console.log("Forgot password email sent to:", email);
  emit("password-reset-sent", email);
}

function handleForgotPasswordError(error) {
  console.log("Forgot password error:", error);
}

function handleEmailSent(email) {
  console.log("Password reset email sent to:", email);
}

function handleGuestLoginSuccess(data) {
  console.log("Guest login successful:", data);
  notify.plain("Welcome! You can now continue with your quote request.");
  emit("guest-login-success", data);
}

function handleGuestLoginError(error) {
  console.log("Guest login error:", error);
  emit("guest-login-error", error);
}

defineExpose({
  goToLogin,
  goToRegister,
  goToEmailVerification,
  goToForgotPassword,
  goToGuestLogin,
  currentView: computed(() => currentView.value),
});
</script>

<style scoped>
.auth-section {
  width: 80%;
  margin: 0 auto;
  height: 100%;
  min-height: 800px;
  overflow-x: hidden;
  border-radius: 14px;
}

/* Override form styles for inline usage */
.auth-section :deep(.login-form),
.auth-section :deep(.register-form),
.auth-section :deep(.email-verification-form),
.auth-section :deep(.forgot-password-form) {
  height: auto;
  min-height: auto;
}

.auth-section :deep(div[style*="height: 100vh"]) {
  height: auto !important;
  min-height: 800px !important;
}

.auth-section :deep(div[style*="width: 50%"]:only-child) {
  width: 100% !important;
}

.auth-section :deep(img[style*="position: absolute"]) {
  display: none;
}

.auth-section :deep(.v-card-text) {
  padding: 0;
}

.auth-section :deep(router-link) {
  display: none;
}

@media (max-width: 768px) {
  .auth-section {
    width: 100%;
  }

  .auth-section :deep(div[style*="width: 50%"]) {
    width: 100% !important;
  }

  .auth-section :deep(.left-panel) {
    margin-top: 0;
  }
}
</style>
